import { FC, useState } from 'react';
import { Button, Col, message, Modal, Popconfirm, Row, Space, Spin, Tooltip, Typography } from 'antd';
import { HiOutlineMail } from 'react-icons/hi';
import { DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import EmptyHorizontal from 'components/EmptyHorizontal';
import TextCopyable from 'components/TextCopyable';
import ENVIRONMENT from 'config/environment';
import copyToClipboard from 'utils/clipboard';

/**
 * Mirrors the link the backend puts in the invitation email (SendInvite::getActions):
 * invites already tied to an existing user point to the client login, every other
 * invite points to the client register page with the invite uuid.
 */
const getInviteLink = (invite: Record<string, any>): string | undefined => {
  if (!ENVIRONMENT.CLIENT_URL || !invite?.uuid) return undefined;

  const baseUrl = ENVIRONMENT.CLIENT_URL.replace(/\/+$/, '');

  return invite.user ? `${baseUrl}/login` : `${baseUrl}/register?invite_id=${invite.uuid}`;
};

const onCopyInviteLinkHandler = async (invite: Record<string, any>) => {
  const link = getInviteLink(invite);

  if (!link) {
    message.error('No invite link available for this invite');
    return;
  }

  const copied = await copyToClipboard(link);

  if (copied) {
    message.success('Invite link copied');
    return;
  }

  // The browser can refuse the copy (lost focus, insecure context), so show the
  // link to be copied by hand instead of losing it.
  Modal.info({
    title: 'Invite link',
    okText: 'Close',
    content: (
      <Typography.Paragraph copyable={{ text: link }} style={{ wordBreak: 'break-all', marginBottom: 0 }}>
        {link}
      </Typography.Paragraph>
    ),
  });
};

interface PendingInvitesCardProps {
  pendingInvites?: Record<string, any>[];
  fetching?: boolean;
  onDeletePendingInvite: (invite: any) => Promise<void>;
  onResendPendingInvite: (invite: any) => void;
}

const PendingInvitesCard: FC<PendingInvitesCardProps> = ({
  pendingInvites,
  fetching,
  onDeletePendingInvite,
  onResendPendingInvite,
}) => {
  const [deleting, setDeleting] = useState(false);

  const onDeleteInviteHandler = (invite: any) => {
    setDeleting(true);

    onDeletePendingInvite(invite).finally(() => {
      setDeleting(false);
    });
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="mb-s" style={{ fontSize: 16, fontWeight: 500 }}>
        Pending Invites
      </div>

      <Spin spinning={fetching}>
        <Space direction="vertical" className="w-full">
          {pendingInvites?.map((invite) => (
            <div key={invite.uuid} style={{ border: '1px solid #0044CC33', borderRadius: 4, padding: '8px 12px' }}>
              <Row wrap={false} gutter={[6, 0]} align="middle" className="mb-s">
                <Col flex="none" style={{ width: 85, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HiOutlineMail style={{ fontSize: 16 }} />
                  Email:
                </Col>

                <Col flex="auto" className="secondary">
                  <TextCopyable>{invite.email}</TextCopyable>
                </Col>

                <Col flex="none" className="secondary">
                  <Tooltip title="Copy invite link">
                    <Button
                      type="link"
                      size="small"
                      className="primary"
                      aria-label="Copy invite link"
                      icon={<LinkOutlined />}
                      onClick={() => onCopyInviteLinkHandler(invite)}
                    />
                  </Tooltip>

                  <Button type="link" size="small" className="primary" onClick={() => onResendPendingInvite(invite)}>
                    Resend
                  </Button>

                  <Popconfirm
                    placement="right"
                    onConfirm={() => onDeleteInviteHandler(invite)}
                    title="Do you want to cancel this invite?"
                  >
                    <Button size="small" type="link" loading={deleting} danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Col>
              </Row>
            </div>
          ))}

          {pendingInvites?.length === 0 && <EmptyHorizontal description="There is no pending invites" />}
        </Space>
      </Spin>
    </div>
  );
};

export default PendingInvitesCard;
